import { useEffect, useRef } from 'react';
import './hero-book-badge.css';

/**
 * トップのビルボード内に置く、学研『執事のマナーと教養図鑑』の購入導線。
 * 書影・文言・CTA は Amazon への1本のリンクにまとめ、二重リンクを作らない。
 */
const PURCHASE_URL = 'https://www.amazon.co.jp/dp/4058027606';
const COVER_ALT = '学研刊『執事のマナーと教養図鑑』の表紙——紅茶のトレイを持つ執事のイラスト。全日本執事協会 監修';
const COVER_ALT_EN = "Cover of Gakken's A British Butler's Guide to Manners and Cultures, illustrated with a butler carrying a tea tray and supervised by the Japan Butler Association";
const FIXED_TOP_MIN_WIDTH = 1024;
const FIXED_TOP_MAX_WIDTH = 1399;
const VIEWPORT_GAP = 24;
const BLOCKER_GAP = 12;
const HORIZONTAL_GAP = 8;

type HeroBookBadgeProps = {
  language?: 'ja' | 'en';
};

export default function HeroBookBadge({ language = 'ja' }: HeroBookBadgeProps) {
  const ref = useRef<HTMLElement>(null);
  const isEnglish = language === 'en';

  useEffect(() => {
    const badge = ref.current;
    if (!badge) return;
    const billboard = badge.closest<HTMLElement>('.home-hero-billboard');
    if (!billboard) return;

    let frame = 0;
    const place = () => {
      if (
        window.innerWidth >= FIXED_TOP_MIN_WIDTH &&
        window.innerWidth <= FIXED_TOP_MAX_WIDTH
      ) {
        badge.style.removeProperty('--hero-book-top');
        badge.style.removeProperty('--hero-book-bottom');
        return;
      }

      const billboardRect = billboard.getBoundingClientRect();
      const badgeRect = badge.getBoundingClientRect();
      const miniCtaRect = badge.querySelector<HTMLElement>('.hero-book-mini-cta')?.getBoundingClientRect();
      const hasVisibleMiniCta = Boolean(miniCtaRect && miniCtaRect.width > 0 && miniCtaRect.height > 0);
      const badgeLeft = hasVisibleMiniCta && miniCtaRect
        ? Math.min(badgeRect.left, miniCtaRect.left)
        : badgeRect.left;
      const badgeRight = hasVisibleMiniCta && miniCtaRect
        ? Math.max(badgeRect.right, miniCtaRect.right)
        : badgeRect.right;
      const badgeHeight = badge.offsetHeight;
      const headerBottom = document.querySelector<HTMLElement>('header')?.getBoundingClientRect().bottom ?? 0;
      const minimumTop = Math.max(
        VIEWPORT_GAP,
        headerBottom - billboardRect.top + BLOCKER_GAP,
      );
      const visibleBottom = Math.min(
        billboardRect.height - VIEWPORT_GAP,
        window.innerHeight - billboardRect.top - VIEWPORT_GAP,
      );
      const blockers = Array.from(
        billboard.querySelectorAll<HTMLElement>(
          '[data-hero-kicker], [data-hero-rule], h1, p, [data-hero-cta], [data-hero-scroll-cue]',
        ),
      )
        .map((element) => element.getBoundingClientRect())
        .filter(
          (rect) =>
            rect.width > 0 &&
            rect.right + HORIZONTAL_GAP > badgeLeft &&
            rect.left - HORIZONTAL_GAP < badgeRight,
        )
        .map(
          (rect) =>
            [
              rect.top - billboardRect.top - BLOCKER_GAP,
              rect.bottom - billboardRect.top + BLOCKER_GAP,
            ] as const,
        )
        .sort((a, b) => a[0] - b[0]);

      let top = visibleBottom - badgeHeight;
      let hasRoom = top >= minimumTop;
      for (let guard = 0; hasRoom && guard < 8; guard += 1) {
        const hit = blockers.find(([start, end]) => top < end && top + badgeHeight > start);
        if (!hit) break;
        top = hit[0] - badgeHeight;
        hasRoom = top >= minimumTop;
      }

      if (hasRoom) {
        badge.style.setProperty('--hero-book-top', `${Math.round(top)}px`);
        badge.style.setProperty('--hero-book-bottom', 'auto');
      } else {
        badge.style.removeProperty('--hero-book-top');
        badge.style.removeProperty('--hero-book-bottom');
      }
    };
    const schedulePlacement = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(place);
    };

    place();
    window.addEventListener('resize', schedulePlacement);
    window.addEventListener('load', schedulePlacement);
    document.fonts?.ready.then(schedulePlacement).catch(() => undefined);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', schedulePlacement);
      window.removeEventListener('load', schedulePlacement);
    };
  }, []);

  return (
    <aside ref={ref} className="hero-book-badge" data-hero-book-badge>
      <a
        className="hero-book-link"
        href={PURCHASE_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={isEnglish ? 'View the supervised book on Amazon' : '監修書籍をAmazonで見る'}
      >
        <span className="hero-book-float">
          <span className="hero-book-cover-wrap">
            <span className="hero-book-frame">
              <img
                className="hero-book-cover"
                src="/images/book/gakken-butler-manners-cover.jpg"
                srcSet="/images/book/gakken-butler-manners-cover@2x.jpg 2x"
                width="480"
                height="705"
                alt={isEnglish ? COVER_ALT_EN : COVER_ALT}
                loading="eager"
                decoding="async"
              />
            </span>
            <span className="hero-book-tag" aria-hidden="true">
              {isEnglish ? 'Supervised' : '監修書籍'}
            </span>
            <span className="hero-book-mini-cta">
              {isEnglish ? 'View on Amazon' : 'Amazonで見る'}
              <span className="hero-book-arrow" aria-hidden="true">↗</span>
            </span>
          </span>
          <span className="hero-book-copy">
            <span className="hero-book-eyebrow">
              {isEnglish ? 'Supervised by the Japan Butler Association' : '全日本執事協会 監修'}
            </span>
            <span className="hero-book-title">
              {isEnglish
                ? "A British Butler's Guide to Manners and Cultures (Gakken)"
                : '『執事のマナーと教養図鑑』(学研)'}
            </span>
            <span className="hero-book-summary">
              {isEnglish
                ? 'Butler etiquette and culture in one volume.'
                : '執事の作法と教養を、一冊に。'}
            </span>
            <span className="hero-book-cta">
              {isEnglish ? 'View on Amazon' : 'Amazonで見る'}
              <span className="hero-book-arrow" aria-hidden="true">↗</span>
            </span>
          </span>
        </span>
      </a>
    </aside>
  );
}
